import { ArrowRight, LoaderCircle, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useApp } from '../app/AppContext';
import { Atmosphere } from '../components/Atmosphere';
import { PhaseLabel } from '../components/PhaseLabel';
import { StyleCard } from '../components/StyleCard';
import {
  countInstructionWords,
  styleRefinementInstructionSchema,
} from '../domain/style/refinement';
import { styleProfileSchema, type StyleProfile } from '../domain/style/schema';
import { analytics } from '../services/analytics';
import { getStyleRefiner } from '../services/style-refinement';

export function StyleEditorPage() {
  const { profile, styleReferencePreviews, setStyleProfile } = useApp();
  const navigate = useNavigate();
  const [draft, setDraft] = useState<StyleProfile | null>(profile.styleProfile);
  const [instruction, setInstruction] = useState('');
  const [isRefining, setIsRefining] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const wordCount = countInstructionWords(instruction);

  if (!draft) return <Navigate to="/discover" replace />;

  const refine = async () => {
    const parsedInstruction = styleRefinementInstructionSchema.safeParse(instruction);
    if (!parsedInstruction.success) {
      setError(
        parsedInstruction.error.issues[0]?.message ?? 'Use one instruction of 50 words or fewer.',
      );
      return;
    }
    setIsRefining(true);
    setError('');
    setMessage('');
    try {
      const result = await getStyleRefiner().refine(draft, parsedInstruction.data);
      setDraft(result.profile);
      setInstruction('');
      setMessage('Your new Style Card is ready.');
      analytics.track('style_card_regenerated');
    } catch (refinementError) {
      setError(
        refinementError instanceof Error
          ? refinementError.message
          : 'Style Card refinement is temporarily unavailable.',
      );
    } finally {
      setIsRefining(false);
    }
  };

  const approve = () => {
    const parsed = styleProfileSchema.safeParse(draft);
    if (!parsed.success) {
      setError('The Style Card could not be saved. Please generate it again.');
      return;
    }
    setStyleProfile(parsed.data);
    analytics.track('style_card_approved');
    navigate('/preferences');
  };

  return (
    <div className="style-editor page-pad">
      <Atmosphere variant="pink" />
      <header className="page-intro style-editor-intro">
        <PhaseLabel index="Phase 02">Interpretation / Editable</PhaseLabel>
        <h1>
          This is a reading.
          <br />
          <i>Make it yours.</i>
        </h1>
        <p>
          Click any highlighted style word to understand it. If the reading feels off, tell STYLE
          what to change in one short note.
        </p>
      </header>

      <main className="style-editor-simple">
        <StyleCard profile={draft} previews={styleReferencePreviews} showAdjustAction={false} />

        <section className="style-refinement-panel" aria-labelledby="style-refinement-heading">
          <div className="style-refinement-heading">
            <span>
              <Sparkles aria-hidden="true" /> One simple edit
            </span>
            <h2 id="style-refinement-heading">What do you want changed?</h2>
            <p>
              Describe the direction in up to 50 words. STYLE will generate a fresh card with your
              instruction in mind.
            </p>
          </div>

          <label className="field refinement-field">
            <span>Your instruction</span>
            <textarea
              value={instruction}
              maxLength={400}
              rows={4}
              placeholder="For example: Make it softer and more romantic, with less geometric structure."
              aria-describedby="refinement-word-count"
              onChange={(event) => {
                setInstruction(event.target.value);
                setError('');
                setMessage('');
              }}
            />
          </label>

          <div className="refinement-actions">
            <span id="refinement-word-count" className={wordCount > 50 ? 'over-limit' : ''}>
              {wordCount} / 50 words
            </span>
            <button
              className="button button-primary button-large"
              type="button"
              disabled={isRefining || wordCount === 0 || wordCount > 50}
              onClick={refine}
            >
              {isRefining ? (
                <>
                  <LoaderCircle className="spin" /> Generating…
                </>
              ) : (
                <>
                  <Sparkles /> Generate a new card
                </>
              )}
            </button>
          </div>
          <small>This edit is text-only. Your reference images are not uploaded again.</small>
        </section>

        {error && (
          <div className="error-banner" role="alert">
            {error}
          </div>
        )}
        {message && (
          <p className="success-banner" role="status">
            {message}
          </p>
        )}
      </main>

      <div className="sticky-continue">
        <button
          className="button button-primary button-large"
          type="button"
          disabled={isRefining}
          onClick={approve}
        >
          Approve Style Card <ArrowRight />
        </button>
      </div>
    </div>
  );
}
